import { useState, useEffect, useCallback } from "react";
import {
  getSalesListApi,
  getExpenseListApi,
  getMileageListApi,
  getBankStatementsListApi,
} from "../services/apiCall";

export const useDashboardData = (businessId, startDate, endDate) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [sales, setSales] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [mileage, setMileage] = useState([]);
  const [bankStatements, setBankStatements] = useState([]);

  const [aggregates, setAggregates] = useState({
    totalRevenue: 0,
    totalExpenses: 0,
    netProfit: 0,
    totalVat: 0,
    moneyInBank: 0,
    unpaidInvoices: 0,
    personallyPaidExpenses: 0,
  });

  const fetchData = useCallback(async () => {
    if (!businessId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const params = { businessId, startDate, endDate };
      const [salesRes, expensesRes, mileageRes, bankRes] = await Promise.all([
        getSalesListApi(params),
        getExpenseListApi(params),
        getMileageListApi(params),
        getBankStatementsListApi(params),
      ]);

      const salesData = salesRes?.data?.sales || [];
      const expensesData = expensesRes?.data?.expenses || [];
      const mileageData = mileageRes?.data?.mileage || [];
      const bankData = bankRes?.data?.statements || [];

      setSales(salesData);
      setExpenses(expensesData);
      setMileage(mileageData);
      setBankStatements(bankData);

      // Calculations
      let revenue = 0;
      let salesVat = 0;
      let unpaidInvs = 0; // Keeping track of unpaid (assuming we had a status field, we'll rough it here or default to 0)

      salesData.forEach((s) => {
        revenue += parseFloat(s.net || 0); // Using net for revenue, or gross if preferred
        salesVat += parseFloat(s.vat || 0);
        // Assuming status 'unpaid' exists, else 0 for now
        if (s.status?.toLowerCase() === "unpaid") {
          unpaidInvs += parseFloat(s.gross || 0);
        }
      });

      let totalExp = 0;
      let expVat = 0;
      expensesData.forEach((e) => {
        totalExp += parseFloat(e.net || 0);
        expVat += parseFloat(e.vat || 0);
      });

      let personalExp = 0;
      mileageData.forEach((m) => {
        personalExp += parseFloat(m.amount || 0);
      });

      let bankBalance = 0;
      bankData.forEach((b) => {
        // Assume bank statements has an absolute balance or we sum amounts
        if (b.transactionType === "CREDIT" || b.amount > 0) {
          bankBalance += parseFloat(b.amount || 0);
        } else {
          bankBalance -= Math.abs(parseFloat(b.amount || 0));
        }
      });

      setAggregates({
        totalRevenue: revenue,
        totalExpenses: totalExp + personalExp, // Adding mileage to expenses
        netProfit: revenue - (totalExp + personalExp),
        totalVat: salesVat - expVat,
        moneyInBank: bankBalance,
        unpaidInvoices: unpaidInvs,
        personallyPaidExpenses: personalExp,
      });
    } catch (err) {
      console.error("Dashboard Data Fetch Error", err);
      setError("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  }, [businessId, startDate, endDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    loading,
    error,
    sales,
    expenses,
    mileage,
    bankStatements,
    aggregates,
    refreshFn: fetchData,
  };
};
